import styled from '@emotion/styled';
import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef } from 'react';

import Button from '@/components/Buttons/Button';
import { ProgressBar } from '@/components/ProgressBar';
import { RequestStatus } from '@/pages/avatar/common';
import { useCreateAvatarStore } from '@/pages/avatar/common/context';
import { AvatarCreateTab } from '@/pages/avatar/common/types';

import { differenceInSeconds } from 'date-fns';
import { useCreateAvatar } from '../../hooks/useCreateAvatar';
import { StepTitle } from '../Styled/Title';
import { calculateUploadTimeEstimation } from './utils';

const Layout = styled.div`
    display: grid;
    grid-template-rows: 1fr 1fr 1fr 1fr;
    height: 100%;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    grid-row: 2;
`;

const Header = styled(StepTitle)`
    font-size: 36px;
`;

const SubHeader = styled(Header)`
    color: ${({ theme }) => theme.colors.black[700]};
`;

const ProgressBarContainer = styled.div`
    margin-top: 52px;
    width: 278px;
`;

const CancelButton = styled(Button)`
    display: flex;
    align-items: center;

    width: 16rem;
    margin: 30px 15px 0 0;
    padding: 5px 16px 4px;
    gap: 6px;

    font-size: 1.6rem;

    border: 1px solid ${({ theme }) => theme.colors.black[300]};
`;

const DEFAULT_EXPECTED_TIME_SECONDS = 2 * 60;
const UPLOAD_BUFFER_TIME_SECONDS = 30;

export const WaitForUploadsStep = observer(() => {
    const createAvatarStore = useCreateAvatarStore();
    const createAvatar = useCreateAvatar(createAvatarStore.type);

    const progressBarStartTime = useRef(new Date().getTime());

    const expectedTimeSeconds: number = useMemo(() => {
        if (!createAvatarStore.footageFile) return DEFAULT_EXPECTED_TIME_SECONDS;

        // limiting with 0 since sometimes estimated time is less than time passed
        const footageUploadEstimation = Math.max(
            0,
            calculateUploadTimeEstimation(createAvatarStore.footageFile) -
                differenceInSeconds(new Date(), createAvatarStore?.footageUploadTime ?? new Date())
        );
        return UPLOAD_BUFFER_TIME_SECONDS + footageUploadEstimation;
    }, [createAvatarStore.footageFile]);

    useEffect(() => {
        const disposer = when(
            () => [createAvatarStore.footageReady, createAvatarStore.consentReady].every(Boolean),
            () => createAvatar.mutate()
        );

        return disposer;
    }, []);

    useEffect(() => {
        const disposer = when(
            () => createAvatarStore.footageUploadStatus === RequestStatus.ERROR,
            () => createAvatarStore.setTab(AvatarCreateTab.FOOTAGE)
        );

        return disposer;
    }, []);

    useEffect(() => {
        const disposer = when(
            () => createAvatarStore.consentValidationStatus === RequestStatus.ERROR,
            () => createAvatarStore.setTab(AvatarCreateTab.CONSENT)
        );

        return disposer;
    }, []);

    const handleCancel = () => {
        createAvatarStore.setTab(AvatarCreateTab.WAIT_FOR_UPLOADS - 1);
    };

    return (
        <>
            <Layout>
                <Container>
                    <Header>Uploading your footage.</Header>
                    <SubHeader>This won't take long.</SubHeader>
                    <ProgressBarContainer>
                        <ProgressBar
                            duration={expectedTimeSeconds}
                            startedAt={progressBarStartTime.current}
                            calcFormula={() => expectedTimeSeconds}
                            updateIntervalMS={100}
                            minProgressShown={1}
                            theme="light"
                            textVariant="text-aside"
                        />
                    </ProgressBarContainer>
                    <CancelButton variant="outlined" color="secondary" onClick={handleCancel} aria-label="Back">
                        Cancel
                    </CancelButton>
                </Container>
            </Layout>
        </>
    );
});
